import {
  ACESFilmicToneMapping,
  Box3,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import type { MeshStandardMaterial, SkinnedMesh } from 'three';
import type { Assets } from './assets';
import { tintOutfit, type ArmorVisual } from './body';
import { decorateItem } from './rarity-fx';

const SIZE = 128;

/**
 * Ikony przedmiotów renderowane z tych samych modeli 3D, z efektem rzadkości.
 * Dzięki temu ikona zawsze pasuje do tego, co widać na postaci. Wyniki są cache'owane.
 */
export class IconRenderer {
  private renderer: WebGLRenderer | null = null;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(30, 1, 0.01, 50);
  private readonly cache = new Map<string, string>();

  constructor(private readonly assets: Assets) {
    this.scene.add(new HemisphereLight('#ffffff', '#6a5644', 3));
    const key = new DirectionalLight('#ffffff', 3.2);
    key.position.set(2, 3, 4);
    this.scene.add(key);
  }

  private ensureRenderer(): WebGLRenderer {
    if (!this.renderer) {
      this.renderer = new WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
      this.renderer.setSize(SIZE, SIZE);
      this.renderer.toneMapping = ACESFilmicToneMapping;
      // Jaśniej niż scena gry: ikony leżą na ciemnych slotach, ciemne przedmioty (czarny hełm) prawie znikały.
      this.renderer.toneMappingExposure = 1.6;
    }
    return this.renderer;
  }

  /**
   * Ikona części stroju (armor-models.json): ten sam model i to samo barwienie co na postaci, lekko z boku.
   * Płaskie rysunki zbroi nie przypominały tego, co bohater faktycznie nosi.
   */
  armor(path: string, look: Pick<ArmorVisual, 'color' | 'trim' | 'material' | 'tier' | 'rarity'>, skinTone: string): string {
    const cacheKey = `armor|${path}|${look.color}|${look.trim ?? ''}|${look.tier}|${skinTone}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;
    const renderer = this.ensureRenderer();
    const obj = SkeletonUtils.clone(this.assets.template(path).scene);
    obj.traverse((o) => {
      const part = o as SkinnedMesh;
      if (!part.isMesh) return;
      const mat = part.material;
      part.material = Array.isArray(mat)
        ? mat.map((m) => tintOutfit(m as MeshStandardMaterial, look, skinTone))
        : tintOutfit(mat as MeshStandardMaterial, look, skinTone);
      part.frustumCulled = false;
    });
    obj.rotation.y = -0.45;
    this.scene.add(obj);
    obj.updateMatrixWorld(true);
    let box = new Box3().setFromObject(obj, true);
    let size = box.getSize(new Vector3());
    const wide = size.x > size.y * 3;
    if (wide) {
      // Karwasze to obie ręce rozłożone w poziomie — obracamy do pionu i kadrujemy jedno przedramię z dłonią.
      obj.rotation.set(0, 0, -Math.PI / 2);
      obj.updateMatrixWorld(true);
      box = new Box3().setFromObject(obj, true);
      size = box.getSize(new Vector3());
    }
    const center = box.getCenter(new Vector3());
    let radius = size.length() / 2;
    if (wide) {
      center.y = box.max.y - size.y * 0.2;
      radius = size.y * 0.15;
    }
    const dist = radius / Math.sin((this.camera.fov * Math.PI) / 360);
    this.camera.position.set(center.x, center.y + radius * 0.15, center.z + dist * 0.95);
    this.camera.lookAt(center);
    renderer.render(this.scene, this.camera);
    const url = renderer.domElement.toDataURL('image/png');
    obj.removeFromParent();
    this.cache.set(cacheKey, url);
    return url;
  }

  icon(visual: string, color: string, tier: number): string {
    const cacheKey = `${visual}|${tier}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    const acc = this.assets.accessory(visual);
    if (!acc) return '';
    this.ensureRenderer();

    const mesh = acc.object.clone();
    mesh.position.set(0, 0, 0);
    mesh.quaternion.identity();
    mesh.rotation.set(0.25, -0.7, Math.PI / 5);
    this.scene.add(mesh);
    decorateItem(mesh, color, tier)?.(0.5);

    // Kadrowanie: kamera tak daleko, żeby cały przedmiot się zmieścił.
    const box = new Box3().setFromObject(mesh);
    const center = box.getCenter(new Vector3());
    const radius = box.getSize(new Vector3()).length() / 2;
    const dist = radius / Math.sin((this.camera.fov * Math.PI) / 360);
    this.camera.position.set(center.x, center.y, center.z + dist * 1.05);
    this.camera.lookAt(center);

    this.renderer!.render(this.scene, this.camera);
    const url = this.renderer!.domElement.toDataURL('image/png');
    mesh.removeFromParent();
    this.cache.set(cacheKey, url);
    return url;
  }
}
